import { SaveOutlined } from "@ant-design/icons";
import { Button, Col, Divider, Form, Modal, Row, Segmented, Select, message } from "antd";
import { useEffect, useState } from "react";
import { PRACTICE_AREA } from "../../domain/user.domain";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { ehrOptions, referralSource } from "../../pages/onboarding/onboarding.constant";
import { getOnboardingCatalog, getUserInfo, patchUserInfo } from "../../service/auth.service";
import { setShowSettingsModal } from "../../slices/appStateSlice";
import { setUserInfo } from "../../slices/authSlice";
import { OnboardingData, OnboardingFormData } from "../../types/index.type";
import AutoShareManager from "../AutoShareManager/AutoShareManager.component";

const Settings = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const { showSettingsModal } = useAppSelector((state) => state.appState);
  const [practiceAreas, setPracticeAreas] = useState<{ label: string; value: string }[]>([]);
  const [selectedPracticeArea, setSelectedPracticeArea] = useState<string | undefined>("behavioral_health");
  const [specialtiesByArea, setSpecialtiesByArea] = useState<Record<string, { label: string; value: string }[]>>({});
  const { userInfo } = useAppSelector((state) => state.auth);

  useEffect(() => {
    (async () => {
      try {
        if (showSettingsModal) {
          const data = await getOnboardingCatalog();
          const pa = (data.practice_areas || []).map((p) => ({ label: p.label, value: p.code }));
          const sba: Record<string, { label: string; value: string }[]> = {};
          Object.entries(data.specialties_by_area || {}).forEach(([area, list]) => {
            sba[area] = (list as any[]).map((s: any) => ({ label: s.label, value: s.code }));
          });
          setPracticeAreas(pa);
          setSpecialtiesByArea(sba);
          setFormValues();
        }
      } catch (e) {
        console.error("Failed to load onboarding catalogs", e);
      }
    })();
  }, [showSettingsModal]);

  const setFormValues = () => {
    form.setFieldsValue({
      practice_area: userInfo.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH ? "behavioral_health" : "medicine",
      specialty: userInfo.specialty_code || "",
      referral_source: userInfo.onboarding_data?.referral_source,
      ehr: userInfo.onboarding_data?.ehr,
      work_as: userInfo.onboarding_data?.work_as,
    });
    if (userInfo.practice_area === PRACTICE_AREA.BEHAVIORAL_HEALTH) {
      setSelectedPracticeArea("behavioral_health");
    } else {
      setSelectedPracticeArea("medicine");
    }
  };

  const handleSave = async (values: OnboardingFormData) => {
    try {
      const onboardingData: OnboardingData = {
        specialty: values.specialty,
        referral_source: values.referral_source,
        ehr: values.ehr,
        work_as: values.work_as,
        completed: true,
        start_time: new Date().toISOString(),
      };

      await patchUserInfo({
        practice_area: values.practice_area,
        onboarding_data: onboardingData,
      });

      message.success("Profile updated successfully!");
      const response = await getUserInfo();
      const data = await response.data;
      dispatch(setUserInfo(data));
    } catch (error) {
      console.error("Error updating onboarding data:", error);
      message.error("Failed to update profile. Please try again.");
    }
  };

  return (
    <Modal
      footer={null}
      title="Settings"
      open={showSettingsModal}
      onCancel={() => dispatch(setShowSettingsModal(false))}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          work_as: "solo",
        }}
        onFinish={handleSave}
      >
        <Form.Item
          name="practice_area"
          label="My practice area is"
          rules={[{ required: true, message: "Please select your practice area" }]}
        >
          <Select disabled>
            {practiceAreas.map((opt: { label: string; value: string }) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="specialty"
          label="My specialty is"
          rules={[{ required: true, message: "Please select your specialty" }]}
        >
          <Select placeholder="Select your specialty" disabled={!selectedPracticeArea}>
            {(specialtiesByArea[selectedPracticeArea || "behavioral_health"] || []).map(
              (opt: { label: string; value: string }) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ),
            )}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={24} md={12}>
            <Form.Item
              name="referral_source"
              label="How did you hear about us?"
              rules={[{ required: true, message: "Please select the referral source" }]}
            >
              <Select placeholder="Select referral source">
                {referralSource.map((opt: { label: string; value: string }) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={24} md={12}>
            <Form.Item
              name="ehr"
              label="What EHR do you use?"
              rules={[{ required: true, message: "Please select your EHR system" }]}
            >
              <Select showSearch optionFilterProp="children" placeholder="Select your EHR system">
                {ehrOptions.map((opt: { label: string; value: string }) => (
                  <Select.Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="work_as"
          label="How many providers would use this software in your practice?"
          rules={[{ required: true, message: "Please select any one option" }]}
          // className={styles[`${componentName}__form-item`]}
        >
          <Segmented
            block
            options={[
              { label: "Solo", value: "solo" },
              { label: "2-10", value: "2-10" },
              { label: "11-50", value: "11-50" },
              { label: "50+", value: "50+" },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Button icon={<SaveOutlined />} style={{ width: "100%" }} type="primary" htmlType="submit">
            Save
          </Button>
        </Form.Item>
      </Form>

      {/* Patient auto-sharing — group users only (needs teammates to share with). */}
      {showSettingsModal && !!userInfo.group_name && (
        <>
          <Divider>Patient sharing</Divider>
          <AutoShareManager />
        </>
      )}
    </Modal>
  );
};

export default Settings;
